import React, { useState } from 'react';
import { useT } from '../i18n.jsx';

const validPrice = value => typeof value === 'number' && Number.isFinite(value) && value >= 0;
const quoteRows = quotes => Array.isArray(quotes?.models)
  ? quotes.models.filter(row => row && typeof row.modelName === 'string' && row.modelName.trim())
  : [];

const validRatio = ratio => typeof ratio === 'number' && Number.isFinite(ratio) && ratio > 0;

export const hasModelReferenceQuotes = quotes =>
  quotes?.currency === 'USD' && ['per_1m_tokens', 'per_model'].includes(quotes?.unit) &&
  ['upstream_cost', 'configured_rate'].includes(quotes?.settlement) &&
  quoteRows(quotes).some(row => (validRatio(quotes.markupRatio) && (validPrice(row.inputPer1M) || validPrice(row.outputPer1M))) || validPrice(row.referenceInputPer1M) || validPrice(row.referenceOutputPer1M) || (Array.isArray(row.rates) && row.rates.some(r => priceUnit(r.unit) && validPrice(r.referencePrice))));

export const priceUnit = unit => ({ character: [1e6, '1M characters'], token: [1e6, '1M tokens'], second: [60, 'minute'], hour: [1, 'hour'] }[unit]);
export const audioPriceText = (rates, direction, reference = false) => {
  const rate = (Array.isArray(rates) ? rates : []).find(r => r.direction === direction && priceUnit(r.unit));
  if (!rate) return '—';
  const value = reference ? rate.referencePrice : rate.price;
  if (!validPrice(value)) return '—';
  const [scale, unit] = priceUnit(rate.unit);
  if (!Number.isFinite(value * scale)) return '—';
  return `$${(value * scale).toLocaleString('en-US', { maximumSignificantDigits: 8 })} / ${unit}`;
};

/** Read-only display contract, independent of provider and interface names. */
export const ModelReferencePrices = ({ quotes }) => {
  const { t, locale } = useT();
  const [expanded, setExpanded] = useState(false);
  if (!hasModelReferenceQuotes(quotes)) return null;
  const rows = quoteRows(quotes);
  const explanation = quotes.unit === 'per_model'
    ? quotes.settlement === 'configured_rate' ? 'characterExplanation' : validRatio(quotes.markupRatio) ? 'audioExplanation' : 'audioUnknownExplanation'
    : validRatio(quotes.markupRatio) ? 'explanation' : 'unknownExplanation';
  const shown = expanded ? rows : rows.slice(0, 5);
  const money = value => validPrice(value)
    ? `$${value.toLocaleString('en-US', { maximumSignificantDigits: 8 })}`
    : '—';
  const updated = value => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isFinite(date.getTime())
      ? date.toLocaleString(locale === 'zh' ? 'zh-CN' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      : '—';
  };
  return (
    <section aria-label={t('api.referencePrices.title')} className="hairline" style={{ borderRadius: 6, overflow: 'hidden', background: 'var(--bg-2)' }}>
      <div style={{ padding: 10, borderBottom: '1px solid var(--line)' }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{t('api.referencePrices.title')}</div>
        <p style={{ margin: '5px 0 0', fontSize: 12, color: 'var(--fg-3)', lineHeight: 1.6 }}>
          {t(`api.referencePrices.${explanation}`, { ratio: quotes.markupRatio })}
        </p>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table aria-label={t('api.referencePrices.title')} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, whiteSpace: 'nowrap' }}>
          <thead>
            <tr style={{ color: 'var(--fg-4)', textAlign: 'right' }}>
              {[t('api.referencePrices.model'), t(quotes.unit === 'per_model' ? 'api.referencePrices.audioInput' : 'api.referencePrices.input'), t(quotes.unit === 'per_model' ? 'api.referencePrices.audioOutput' : 'api.referencePrices.output'), ...(rows.some(r => r.rates || 'referenceInputPer1M' in r) ? [t('api.referencePrices.official')] : []), t('api.referencePrices.updated')].map((label, i) =>
                <th key={label} scope="col" style={{ padding: '8px 10px', textAlign: i === 0 ? 'left' : 'right', fontWeight: 500 }}>{label}</th>)}
            </tr>
          </thead>
          <tbody>
            {shown.map(row => (
              <tr key={row.modelName} style={{ borderTop: '1px solid var(--line)', textAlign: 'right' }}>
                <th scope="row" className="mono" style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 400 }}>{row.modelName}</th>
                <td className="mono" style={{ padding: '8px 10px', color: 'var(--ok)' }}>{quotes.unit === 'per_model' ? audioPriceText(row.rates, 'input') : money(row.inputPer1M)}</td>
                <td className="mono" style={{ padding: '8px 10px', color: 'var(--warn)' }}>{quotes.unit === 'per_model' ? audioPriceText(row.rates, 'output') : money(row.outputPer1M)}</td>
                {rows.some(r => r.rates || 'referenceInputPer1M' in r) && <td style={{ padding: '8px 10px' }}>{quotes.unit === 'per_model' ? [audioPriceText(row.rates, 'input', true), audioPriceText(row.rates, 'output', true)].filter(p => p !== '—').join(' · ') || '—' : `${money(row.referenceInputPer1M)} / ${money(row.referenceOutputPer1M)}`}</td>}
                <td style={{ padding: '8px 10px', color: 'var(--fg-4)' }}>{updated(row.referenceUpdatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 5 && <button type="button" className="btn btn-sm btn-ghost" style={{ margin: 8 }} onClick={() => setExpanded(value => !value)}>
        {expanded ? t('api.referencePrices.collapse') : t('api.referencePrices.expand', { count: rows.length })}
      </button>}
    </section>
  );
};
